import React, { useMemo } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import {
  GfFigmaTemplateData,
  GfFlyerItemComponentPosition,
} from "@swagger/typescript-fetch-goflyer";
import {
  CustomAutocompleteMultiSelect,
  AutocompleteOption,
} from "../../components/CustomAutocomplete";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import {
  StyledDialog,
  ModalTitleBar,
  TitleWrap,
  SummaryCard,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  ActionsBar,
  PrimaryButton,
  SecondaryButton,
} from "./styled/EditTemplateModal-styled";
import { useEditTemplateModal } from "./hooks/useEditTemplateModal";
import { Box } from "@mui/material";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";

export interface EditTemplateModalContentProps {
  open: boolean;
  onClose: () => void;
  item?: GfFlyerItemComponentPosition;
  onUpdated?: () => void;
}

export const EditTemplateModal: React.FC<EditTemplateModalContentProps> = ({
  open,
  onClose,
  item,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const {
    options,
    selectedTemplate,
    handleTemplateSelect,
    loading,
    saving,
    error,
    handleSearch,
    handleSave,
  } = useEditTemplateModal({ open, item, onClose, onUpdated });

  const readOnlyRows = useMemo(() => {
    return [
      {
        label: t(messages["Store"]()),
        value:
          (item?.store?.merchant?.name || "") +
          (item?.store?.signature ? ` - ${item?.store?.signature}` : ""),
      },
      {
        label: t(messages["Category"]()),
        value:
          item?.flyerItemCategory?.name ||
          (item?.frontPage ? "Front-Page" : ""),
      },
      {
        label: t(messages["Front_Page"]()),
        value: item?.frontPage ? "Yes" : "No",
      },
      {
        label: t(messages["Count"]()),
        value: item?.flyerItemCount ?? "",
      },
    ];
  }, [item, t]);

  const disableSave =
    saving ||
    !selectedTemplate ||
    selectedTemplate?.id === item?.figmaTemplateData?.id;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      aria-labelledby="edit-template-title"
      aria-describedby="edit-template-description"
    >
      <DialogTitle id="edit-template-title" component={ModalTitleBar as any}>
        <TitleWrap>
          <Typography
            variant="h6"
            component="h2"
            data-testid="edit-template-modal-title"
          >
            {t(messages.Edit_Template())}
          </Typography>
          <Typography
            id="edit-template-description"
            variant="body2"
            color="text.secondary"
          >
            {t(messages.Choose_Figma_Template_For_Component_Position())}
          </Typography>
        </TitleWrap>
      </DialogTitle>

      <DialogContent dividers aria-busy={saving}>
        <Stack spacing={2} sx={{ overflow: "hidden" }}>
          <SummaryCard role="group" aria-label="Current selection details">
            <Stack spacing={1.25}>
              {readOnlyRows.map((row, i) => (
                <SummaryRow key={i}>
                  <SummaryLabel>{row.label}</SummaryLabel>
                  <Divider flexItem orientation="vertical" />
                  <SummaryValue
                    data-testid={`edit-template-${row.label.toLowerCase()}-value`}
                  >
                    {String(row.value ?? "-")}
                  </SummaryValue>
                </SummaryRow>
              ))}
            </Stack>
          </SummaryCard>

          {!loading && error && (
            <Typography
              color="error"
              role="alert"
              data-testid="edit-template-error-message"
            >
              {error}
            </Typography>
          )}

          <Box sx={{ minHeight: "100px" }}>
            <CustomAutocompleteMultiSelect<GfFigmaTemplateData>
              title={"Template"}
              optionsList={options as AutocompleteOption<GfFigmaTemplateData>[]}
              selectedValues={
                selectedTemplate
                  ? [
                      {
                        label:
                          selectedTemplate.name ||
                          selectedTemplate.id ||
                          "Unnamed",
                        value: selectedTemplate,
                      },
                    ]
                  : []
              }
              multiSelect={false}
              isLoading={loading}
              handleOnSearch={handleSearch}
              handleOnChange={handleTemplateSelect}
              dataTestId="template-autocomplete"
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <ActionsBar>
          <SecondaryButton
            onClick={onClose}
            variant="outlined"
            color="inherit"
            aria-label="Cancel"
            data-testid="edit-template-cancel-button"
          >
            {t(messages.Cancel())}
          </SecondaryButton>
          <PermissionComponentWrapper
            requiredPermission={["edit"]}
            requiredPermissionGroup={["figmaComponentLayout"]}
          >
            <PrimaryButton
              onClick={handleSave}
              variant="contained"
              disabled={disableSave}
              aria-disabled={disableSave}
              data-testid="edit-template-save-button"
            >
              {t(messages.Save())}
            </PrimaryButton>
          </PermissionComponentWrapper>
        </ActionsBar>
      </DialogActions>
    </StyledDialog>
  );
};

export default EditTemplateModal;
